/**
 * Every date the app prints, in one module.
 *
 * An exploration is used across days, and the study behind this app never
 * tested that. Walking one exploration across a week under a moved clock found
 * the dates in four conventions and two time zones: a card said a check made at
 * 07:20 on the 14th was made on the 13th, because it printed the UTC day
 * (`toISOString().slice(0, 10)`, copied into a dozen files), while the prompt's
 * "Today is" line, the result form's own parser and the fork line all read the
 * reader's calendar; the result form then offered yesterday as its default and
 * its latest allowed day, so anyone east of UTC before breakfast recorded a
 * result a day early and could not pick today; the replay printed the
 * browser's locale ("9/14/2026, 7:30:14 AM") beside rows that print ISO days; and
 * the Library said "saved 3 hours ago" beside a sidebar row that said "now"
 * about the same save.
 *
 * So the rule is one sentence: A DAY IS THE READER'S CALENDAR DAY. That is the
 * day they would write on the page, the day `<input type="date">` hands back, and
 * the day `dateLine` tells every pass it is. Printed as `YYYY-MM-DD` wherever a
 * surface prints a whole day on a record (a check, a result, a run, a survey),
 * because that is what those surfaces already printed and it sorts; spelled
 * "24 Sep" only on a row's second line, where `shortDay` already was.
 *
 * The prompts read it too: `dateLine` tells a pass what today is, and
 * `recordDay` in `recordDigest.ts`, `fmtDay` in `prompts.ts` and Ask's check
 * marks date the records it reads, so a check made ten minutes ago is never
 * yesterday's to the model while it is today's on the card. What does NOT read this, deliberately: every machine-readable timestamp
 * (the manifest's), which is an instant and not a day.
 *
 * No runtime imports, so anything in `core/` or `ui/` can read it without joining
 * a cycle.
 */

export const MINUTE_MS = 60_000
export const HOUR_MS = 60 * MINUTE_MS
export const DAY_MS = 24 * HOUR_MS

/** How long a relative age stays hours: a document touched yesterday afternoon
 *  is still hours-ago work, and hours are what tell you whether to go back to it
 *  (the rule the explorations list always used). */
export const AGO_HOURS_UNTIL = 48
/** How long a relative age stays days before it becomes a date. Past a week,
 *  "23d ago" is arithmetic the reader has to do; "22 Aug" is the answer. */
export const AGO_DAYS_UNTIL = 7

const pad = (n: number) => String(n).padStart(2, '0')

/** The reader's calendar day, `YYYY-MM-DD`, in their own time zone. */
export function calendarDay(t: number): string {
  const d = new Date(t)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * The reader's calendar year: the year `calendarDay` prints for the same instant.
 *
 * For a comparison a surface prints beside a day. A conjecture registered at
 * 23:30 on 31 December in New York printed "registered 2026-12-31" while its
 * later-statement rule read the UTC year, 2027, and so would not call a page
 * dated 2027 later — two calendars in one sentence (D287).
 */
export function calendarYear(t: number): number {
  return new Date(t).getFullYear()
}

/** The reader's calendar day and minute, `YYYY-MM-DD HH:MM` — for a surface that
 *  lists several events on one day and has to tell them apart. */
export function calendarMinute(t: number): string {
  const d = new Date(t)
  return `${calendarDay(t)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** The same minute as a `datetime-local` input's value (`YYYY-MM-DDTHH:MM`). */
export function minuteInputValue(t: number): string {
  return calendarMinute(t).replace(' ', 'T')
}

/**
 * The last millisecond of the minute a `datetime-local` value names, or NaN.
 *
 * The field has no seconds. A board recorded at 07:20:42 lies AFTER the 07:20
 * the field offers as its latest value, so reading the value as the minute's
 * first instant made the newest board unreachable by choosing its own minute.
 * A minute chosen is the whole minute.
 */
export function endOfMinuteInput(value: string): number {
  const start = new Date(value).getTime()
  return Number.isFinite(start) ? start + MINUTE_MS - 1 : Number.NaN
}

/**
 * A `YYYY-MM-DD` from a date field, as that day's local noon — or null.
 *
 * Noon rather than midnight, so the instant stays on the same calendar day
 * wherever it is read from within twelve hours of the zone it was written in.
 */
export function dayInputNoon(value: string): number | null {
  const [y, m, d] = value.split('-').map(Number)
  if (!y || !m || !d) return null
  const at = new Date(y, m - 1, d, 12, 0, 0).getTime()
  return Number.isFinite(at) ? at : null
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * A day as a row prints it: "24 Sep", and "24 Sep 2025" once it is not this
 * year. Spelled from a table rather than the locale's formatter, so a row reads
 * the same on every machine and a test can pin it — the app's copy is English
 * throughout, and "Sep 24" beside "24 Sep" on one screen is two conventions.
 */
export function shortDay(at: number, now = Date.now()): string {
  const d = new Date(at)
  const day = `${d.getDate()} ${MONTHS[d.getMonth()]}`
  return d.getFullYear() === new Date(now).getFullYear() ? day : `${day} ${d.getFullYear()}`
}

/**
 * How long ago, in the one form this app says it: "just now", "12m ago", "30h
 * ago", "3d ago", then the day itself ("22 Aug") once it is a week back.
 *
 * Clamped at zero rather than allowed to run negative: a clock nudged backwards,
 * or a document written by a device a few seconds ahead, must not report a save
 * in the future. Minutes and hours round; days count whole days passed, so 60
 * hours is "2d ago" and never "3d ago" — an age may not claim more time than has
 * gone by.
 */
export function ago(t: number, now = Date.now()): string {
  const ms = Math.max(0, now - t)
  const m = Math.round(ms / MINUTE_MS)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  // Held under the line rather than rounded over it: 47.6 hours is "47h ago",
  // not a "48h" the next rule would print as "1d ago".
  if (ms < AGO_HOURS_UNTIL * HOUR_MS)
    return `${Math.min(AGO_HOURS_UNTIL - 1, Math.round(ms / HOUR_MS))}h ago`
  const d = Math.floor(ms / DAY_MS)
  if (d < AGO_DAYS_UNTIL) return `${d}d ago`
  return shortDay(t, now)
}

/**
 * When `ago(t)` next reads differently, in milliseconds from `now` — so a surface
 * that prints an age can wake exactly then instead of polling. Past the week,
 * the text is a date and changes only at the year, which no open tab waits for.
 */
export function agoChangesIn(t: number, now = Date.now()): number | null {
  const ms = Math.max(0, now - t)
  if (ms < AGO_HOURS_UNTIL * HOUR_MS) {
    const unit = ms < 59.5 * MINUTE_MS ? MINUTE_MS : HOUR_MS
    // Rounded units change half a unit past each whole one, and the hours end
    // at the line where days begin.
    const next = Math.min((Math.floor(ms / unit + 0.5) + 0.5) * unit, AGO_HOURS_UNTIL * HOUR_MS)
    return Math.max(1000, Math.ceil(next - ms))
  }
  if (ms < AGO_DAYS_UNTIL * DAY_MS)
    return Math.max(1000, (Math.floor(ms / DAY_MS) + 1) * DAY_MS - ms)
  return null
}
